import React from 'react';
import Helmet from 'react-helmet';
import { StaticQuery, graphql } from 'gatsby';
import { useLocation } from '@gatsbyjs/reach-router';
import TriDymeIcon from '../../media/TriDymeIconV1.png';
import TriDymeDefaultImage from '../../media/tridyme-default-image.png';
import { translatePath } from '../../utils/localePaths';

const withTrailingSlash = (path) => (path.endsWith('/') ? path : `${path}/`);
const absolute = (siteUrl, path) => (/^https?:/.test(path) ? path : `${siteUrl}${path}`);

// Organization structured data, shown on every page
const organizationJsonLd = (siteUrl) => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'TriDyme',
  legalName: 'TriAzur SAS',
  url: siteUrl,
  logo: absolute(siteUrl, TriDymeIcon),
  email: 'contact@tridyme.com',
  address: {
    '@type': 'PostalAddress',
    streetAddress: '18 rue d’Arras',
    postalCode: '92000',
    addressLocality: 'Nanterre',
    addressCountry: 'FR',
  },
  sameAs: [
    'https://www.linkedin.com/company/27166752/',
    'https://github.com/tridyme',
  ],
});

const SEO = (props) => {
  const { pathname } = useLocation();
  return (
    <StaticQuery
      query={detailsQuery}
      render={(data) => {
        const { siteUrl } = data.site.siteMetadata;
        const title = props.title || data.site.siteMetadata.title;
        const metaDescription = props.description || data.site.siteMetadata.description;
        const metaAuthor = props.author || data.site.siteMetadata.author;
        const path = withTrailingSlash(pathname);
        const canonicalUrl = absolute(siteUrl, path);
        const metaImage = absolute(siteUrl, props.image || TriDymeDefaultImage);
        const frUrl = absolute(siteUrl, withTrailingSlash(translatePath(path, 'fr')));
        const enUrl = absolute(siteUrl, withTrailingSlash(translatePath(path, 'en')));
        const jsonLd = [organizationJsonLd(siteUrl)].concat(props.jsonLd || []);

        return (
          <Helmet
            htmlAttributes={{
              lang: props.lang,
            }}
            title={title}
            link={[
              { rel: 'icon', type: 'image/png', href: TriDymeIcon },
              { rel: 'canonical', href: canonicalUrl },
              { rel: 'alternate', hreflang: 'fr', href: frUrl },
              { rel: 'alternate', hreflang: 'en', href: enUrl },
              { rel: 'alternate', hreflang: 'x-default', href: frUrl },
              { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
              { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossorigin: 'true' },
              { rel: 'stylesheet', href: 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500&display=swap' },
            ]}
            meta={[
              {
                name: 'google-site-verification',
                content: 'HnQ2mgnlHBbVKY-fTEP59rjZCdL-LqjrbCYp7Fy1eVo'
              },
              {
                name: 'viewport',
                content: 'width=device-width, initial-scale=1'
              },
              {
                name: 'description',
                content: metaDescription
              },
              {
                property: 'og:title',
                content: title
              },
              {
                property: 'og:description',
                content: metaDescription
              },
              {
                property: 'og:type',
                content: 'website'
              },
              {
                property: 'og:image',
                content: metaImage
              },
              {
                property: 'og:url',
                content: canonicalUrl
              },
              {
                property: 'og:locale',
                content: props.lang === 'en' ? 'en_GB' : 'fr_FR'
              },
              {
                name: 'twitter:card',
                content: 'summary_large_image'
              },
              {
                name: 'twitter:creator',
                content: metaAuthor
              },
              {
                name: 'twitter:title',
                content: title
              },
              {
                name: 'twitter:description',
                content: metaDescription
              },
              {
                name: 'twitter:image',
                content: metaImage
              },
            ]}
          >
            <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
          </Helmet>
        );
      }}
    />
  );
};

SEO.defaultProps = {
  lang: 'en',
  meta: [],
};

export default SEO;

const detailsQuery = graphql`
  query DefaultSEOQuery {
    site {
      siteMetadata {
        title
        description
        siteUrl
        author
      }
    }
  }
`;
